import { Badge, Group, Text, Tooltip, Progress } from '@mantine/core';
import { Check, X, AlertTriangle } from 'lucide-react';
import type { DataLibraryParticipant } from '../../../types';
import { FLAG_SEVERITY, getFlagSeverity } from '../../../config/severityConfig';
import { formatRelativeTime, formatDate } from '../../../utils/formatters';
import { getTimezoneAbbreviation } from '../../../utils/timezone';
import { GROUP_AVATAR_COLORS } from '../../../utils/groupColors';
import { AttendanceRate, GroupBadge } from '../../common';
import { getAttendanceCoverageDisplay } from '../../../utils/attendanceCoverage';
import { renderCohortHealthBadge } from '../components/CohortHealthBadge';
import { SurveyResponsePill } from '../components/SurveyResponsePill';
import { ParticipantEmailPill } from '../components/ParticipantEmailPill';
import { SessionAttendanceBoxList } from '../components/SessionAttendanceBox';
import { SessionEngagementIndicatorList } from '../components/SessionEngagementIndicator';

const EMAIL_TYPE_LABELS: Record<string, string> = {
  invite: 'Invitation',
  invitation: 'Invitation',
  invitation_email: 'Invitation',
  welcome: 'Welcome',
  password_reset: 'Password Reset',
  reminder: 'Reminder',
  notification: 'Notification',
  verification: 'Verification',
  export_ready: 'Export Ready',
  system: 'System',
};

function formatEmailTypeLabel(emailType: string): string {
  return EMAIL_TYPE_LABELS[emailType] || emailType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const DAY_MAP: Record<string, string> = {
  monday: 'Mondays',
  tuesday: 'Tuesdays',
  wednesday: 'Wednesdays',
  thursday: 'Thursdays',
  friday: 'Fridays',
  saturday: 'Saturdays',
  sunday: 'Sundays',
};

/** Format raw timeslot string ("wednesday 09:00:00 America/New_York") into "Wednesdays 9:00 AM ET" */
function formatTimeslot(raw: string): string {
  const parts = raw.split(' ');
  if (parts.length < 2) return raw;

  const day = DAY_MAP[parts[0].toLowerCase()] || parts[0];

  const timeParts = parts[1].split(':');
  let hours = parseInt(timeParts[0], 10);
  const minutes = timeParts[1] || '00';
  const ampm = hours >= 12 ? 'PM' : 'AM';
  if (hours > 12) hours -= 12;
  if (hours === 0) hours = 12;
  const formattedTime = `${hours}:${minutes} ${ampm}`;

  const timezone = parts.slice(2).join(' ');
  const tz = timezone ? getTimezoneAbbreviation(timezone) : '';

  return `${day} ${formattedTime}${tz ? ` ${tz}` : ''}`;
}

function renderSatisfactionPill(value: string | null) {
  if (!value) return <Text size="sm" c="dimmed">&mdash;</Text>;
  return <SurveyResponsePill value={value} questionType="scale" />;
}

const PARTICIPANT_STATUS_CONFIG: Record<string, { color: string; label: string; variant: string }> =
  {
    pending: { color: 'yellow', label: 'Pending', variant: 'light' },
    active: { color: 'medleyGreen', label: 'Active', variant: 'light' },
    withdrawn: { color: 'gray', label: 'Withdrawn', variant: 'light' },
  };

export function renderDataLibraryCell(
  participant: DataLibraryParticipant,
  columnId: string,
): React.ReactNode {
  switch (columnId) {
    case 'first_name':
      return <Text size="sm">{participant.first_name}</Text>;
    case 'last_name':
      return <Text size="sm">{participant.last_name}</Text>;
    case 'email':
      return (
        <ParticipantEmailPill
          email={participant.email}
          participantId={participant.id}
          cohortId={participant.cohort_id}
        />
      );
    case 'preferred_name':
      return <Text size="sm">{participant.preferred_name || '—'}</Text>;
    case 'cohort_name':
      return <Text size="sm">{participant.cohort_name}</Text>;
    case 'cohort_health':
      return renderCohortHealthBadge(participant.cohort_health);
    case 'partner_name':
      return <Text size="sm">{participant.partner_name || '—'}</Text>;
    case 'status': {
      const statusCfg = PARTICIPANT_STATUS_CONFIG[participant.status];
      return (
        <Badge
          size="xs"
          color={statusCfg?.color || 'gray'}
          variant={(statusCfg?.variant as 'light' | 'filled') || 'light'}
          tt="uppercase"
          styles={{ label: { fontSize: 10, letterSpacing: 0.3 } }}
        >
          {statusCfg?.label || participant.status}
        </Badge>
      );
    }
    case 'group_name':
      return <GroupBadge groupName={participant.group_name} groupId={participant.group_id} />;
    case 'coach_name':
      return <Text size="sm">{participant.coach_name || '—'}</Text>;
    case 'timeslot': {
      if (!participant.timeslot) return <Text size="sm" c="dimmed">&mdash;</Text>;
      const formatted = formatTimeslot(participant.timeslot);
      let hash = 0;
      for (let i = 0; i < participant.timeslot.length; i++) {
        hash = participant.timeslot.charCodeAt(i) + ((hash << 5) - hash);
      }
      const slotColor = GROUP_AVATAR_COLORS[Math.abs(hash) % GROUP_AVATAR_COLORS.length];
      return (
        <Badge
          size="lg"
          radius="xl"
          styles={{
            root: { backgroundColor: `${slotColor}18`, border: `1px solid ${slotColor}40` },
            label: { fontWeight: 500, fontSize: 13, textTransform: 'none', letterSpacing: 0, color: slotColor },
          }}
        >
          {formatted}
        </Badge>
      );
    }
    case 'role':
      return <Text size="sm">{participant.role || '—'}</Text>;
    case 'business_unit':
      return <Text size="sm">{participant.business_unit || '—'}</Text>;
    case 'location':
      return <Text size="sm">{participant.location || '—'}</Text>;
    case 'manager':
      return <Text size="sm">{participant.manager || '—'}</Text>;
    case 'level':
      return <Text size="sm">{participant.level || '—'}</Text>;
    case 'dnm':
      return <Text size="sm">{participant.dnm || '—'}</Text>;
    case 'attendance':
      return <AttendanceCell participant={participant} />;
    case 'attendance_coverage': {
      // MED-1266 (AC12): marking coverage as its own column.
      const d = getAttendanceCoverageDisplay({
        rate: 1,
        marked: participant.total_sessions,
        population: participant.attendance_population,
      });
      return <Text size="sm">{d.isUnrecorded ? '—' : d.isComplete ? 'Complete' : d.coverageLabel}</Text>;
    }
    case 'attendance_detail':
      return <SessionAttendanceBoxList entries={participant.sessions_attendance || []} />;
    case 'avg_engagement':
      return (
        <Text size="sm">
          {participant.avg_engagement !== null ? Number(participant.avg_engagement).toFixed(1) : '—'}
        </Text>
      );
    case 'engagement_detail':
      return <SessionEngagementIndicatorList entries={participant.sessions_engagement || []} />;
    case 'personality_type':
      return <Text size="sm">{participant.personality_type || '—'}</Text>;
    case 'flags':
      return <FlagsCell participant={participant} />;
    case 'flag_severity':
      return participant.flag_severity ? (
        <SeverityDot severity={participant.flag_severity} />
      ) : (
        <Text size="sm" c="dimmed">
          —
        </Text>
      );
    case 'midpoint_completed':
      return <SurveyStatusIcon completed={participant.midpoint_completed} />;
    case 'endpoint_completed':
      return <SurveyStatusIcon completed={participant.endpoint_completed} />;
    case 'satisfaction_score':
      return renderSatisfactionPill(participant.satisfaction_score);
    case 'mid_satisfaction':
      return renderSatisfactionPill(participant.mid_satisfaction);
    case 'end_satisfaction':
      return renderSatisfactionPill(participant.end_satisfaction);
    case 'mid_coach_satisfaction':
      return renderSatisfactionPill(participant.mid_coach_satisfaction);
    case 'end_coach_satisfaction':
      return renderSatisfactionPill(participant.end_coach_satisfaction);
    case 'last_email_date':
      return participant.last_email_date ? (
        <div>
          <Text size="sm" c="dimmed">
            {formatRelativeTime(participant.last_email_date)}
          </Text>
          {participant.last_email_type && (
            <Text fz={11} c="dimmed">
              {formatEmailTypeLabel(participant.last_email_type)} &middot; {formatDate(participant.last_email_date)}
            </Text>
          )}
        </div>
      ) : (
        <Text size="sm" c="dimmed">—</Text>
      );
    case 'enrolled_at':
      return (
        <Text size="sm" c="dimmed">
          {formatRelativeTime(participant.enrolled_at)}
        </Text>
      );
    case 'coaching_experience':
      return <Text size="sm">{participant.coaching_experience || '—'}</Text>;
    case 'coaching_value':
      return (
        <Text size="sm" truncate>
          {participant.coaching_value || '—'}
        </Text>
      );
    default:
      return <Text size="sm">—</Text>;
  }
}

function AttendanceCell({ participant }: { participant: DataLibraryParticipant }) {
  // MED-1266: no sessions in scope at all (population === 0) → "No sessions"; an
  // unrecorded state (population > 0, nothing marked) is handled by AttendanceRate.
  if (participant.attendance_population === 0) {
    return (
      <Text size="sm" c="dimmed">
        No sessions
      </Text>
    );
  }

  const pct = participant.attendance_percentage ?? 0;
  const color = pct >= 80 ? 'medleyGreen' : pct >= 60 ? 'orange' : 'dangerRed';

  return (
    <Group gap={6} wrap="nowrap">
      <Progress value={pct} color={color} size="sm" w={50} />
      {/* MED-1266: rate + marking coverage via the shared component (total_sessions = marked). */}
      <AttendanceRate
        rate={participant.attendance_percentage}
        marked={participant.total_sessions}
        population={participant.attendance_population}
        variant="compact"
        size="xs"
      />
    </Group>
  );
}

function FlagsCell({ participant }: { participant: DataLibraryParticipant }) {
  if (!participant.flags || participant.flags.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  // MED-744: Deduplicate flags by category + reason to prevent duplicate pills
  const seen = new Set<string>();
  const uniqueFlags = participant.flags.filter(flag => {
    const key = `${flag.flag_category}:${flag.flag_reason}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return (
    <Group gap={4} wrap="nowrap">
      {uniqueFlags.slice(0, 3).map(flag => {
        const severity = getFlagSeverity(flag.flag_category);
        const config = FLAG_SEVERITY[severity];
        return (
          <Tooltip key={flag.id} label={flag.flag_reason} withArrow>
            <Badge
              size="xs"
              variant="outline"
              styles={{
                root: {
                  borderColor: config.pill.border,
                  color: config.pill.text,
                  backgroundColor: config.pill.background,
                },
              }}
            >
              {flag.flag_category === 'coach_flag'
                ? 'Coach'
                : flag.flag_category === 'attendance'
                  ? 'Attend'
                  : flag.flag_category === 'participant_feedback'
                    ? 'Feedback'
                    : 'Engage'}
            </Badge>
          </Tooltip>
        );
      })}
      {uniqueFlags.length > 3 && (
        <Text size="xs" c="dimmed">
          +{uniqueFlags.length - 3}
        </Text>
      )}
    </Group>
  );
}

function SeverityDot({ severity }: { severity: 'high' | 'medium' | 'low' }) {
  const config = FLAG_SEVERITY[severity];
  return (
    <Group gap={6}>
      <AlertTriangle size={14} color={config.pill.text} />
      <Text size="xs" tt="capitalize" c={config.pill.text}>
        {severity}
      </Text>
    </Group>
  );
}

function SurveyStatusIcon({ completed }: { completed: boolean }) {
  return completed ? (
    <Check size={16} color="#4A7C59" />
  ) : (
    <X size={16} color="#ccc" />
  );
}
